import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Leaf, TreeDeciduous, Apple, Trees, Heart, CheckCircle, Loader2, Calendar, X, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MINISTRY_BANNER = 'https://static.prod-images.emergentagent.com/jobs/9bd4de2a-403c-4211-ba6a-7e7952c06d35/images/0f840a9da0fc7fdcf5f799cedc92d5d0049c007d763102e1d4304916e6d82131.png';

const GIVING_TIERS = [
  {
    id: 'seed',
    name: 'Seed',
    icon: '🌱',
    amount: 10,
    description: 'Provides groceries for one family per month',
    color: '#22c55e'
  },
  {
    id: 'branch',
    name: 'Branch',
    icon: '🍃',
    amount: 25,
    description: 'Funds one children\'s ministry session',
    color: '#16a34a'
  },
  {
    id: 'fruit',
    name: 'Fruit Bearer',
    icon: '🍎',
    amount: 50,
    description: 'Sponsors community outreach for a week',
    color: '#dc2626',
    popular: true
  },
  {
    id: 'tree',
    name: 'Tree of Life',
    icon: '🌳',
    amount: 100,
    description: 'Covers full ministry operations for one week',
    color: '#7B6B9C'
  }
];

export default function FruitBearersPage() {
  const [selectedTier, setSelectedTier] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(0);

  // Check for return from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    
    if (sessionId) {
      // Poll for payment status
      const pollStatus = async (attempts = 0) => {
        try {
          const response = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
          
          if (response.data.payment_status === 'paid') {
            setPaymentAmount(response.data.amount_total);
            // Find matching tier
            const tier = GIVING_TIERS.find(t => t.amount === response.data.amount_total);
            if (tier) setSelectedTier(tier);
            setSubmitted(true);
            setShowModal(true);
            // Clear the URL parameters
            window.history.replaceState({}, '', '/fruit-bearers');
          } else if (response.data.status === 'expired' || attempts >= 5) {
            toast.error('Payment session expired. Please try again.');
            window.history.replaceState({}, '', '/fruit-bearers');
          } else {
            // Continue polling
            setTimeout(() => pollStatus(attempts + 1), 2000);
          }
        } catch (error) {
          console.error('Error checking payment status:', error);
        }
      };
      
      pollStatus();
    }
  }, []);

  const handleJoin = (tier) => {
    setSelectedTier(tier);
    setShowModal(true);
    setSubmitted(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Create Stripe checkout session
      const response = await axios.post(`${API}/payments/fruit-bearers/checkout`, {
        tier_id: selectedTier.id,
        origin_url: window.location.origin,
        donor_name: formData.name,
        donor_email: formData.email,
        donor_phone: formData.phone
      });
      
      if (response.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedTier(null);
    setFormData({ name: '', email: '', phone: '' });
    setSubmitted(false);
  };

  return (
    <div className="min-h-screen bg-cream pt-20" data-testid="fruit-bearers-page">
      {/* Banner */}
      <div className="w-full">
        <img src={MINISTRY_BANNER} alt="Living Fruit Ministry" className="w-full h-auto" />
      </div>

      {/* Hero Section */}
      <section className="py-16 px-6 bg-gradient-to-b from-green-50 to-cream">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="text-6xl mb-6">🍎</div>
            <h1 className="font-serif text-4xl md:text-5xl mb-4" style={{ color: '#1A1A1A' }}>
              Become a Fruit Bearer
            </h1>
            <p className="text-xl md:text-2xl mb-6" style={{ color: '#1E3A5F' }}>
              Join our family of faithful monthly givers who keep the mission growing all year long.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Monthly */}
      <section className="py-12 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="prose prose-lg max-w-none text-center"
          >
            <p className="text-lg leading-relaxed" style={{ color: '#1E3A5F' }}>
              Some fruit takes time to grow. When you give monthly to Living Fruit Ministry, 
              you become part of something bigger than a single gift — you become a <strong>Fruit Bearer</strong>. 
              Your consistent support allows us to plan, serve, and reach further into our community every single month.
            </p>
            <p className="text-lg leading-relaxed mt-4" style={{ color: '#1E3A5F' }}>
              As a Fruit Bearer, your giving never stops working. While you sleep, while you work, 
              while you worship — your generosity is feeding families, empowering children, and keeping 
              the lights on for those who need us most.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Giving Tiers */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-serif text-3xl mb-4" style={{ color: '#1A1A1A' }}>
              Choose Your Giving Level
            </h2>
            <p style={{ color: '#1E3A5F' }}>
              Every tier makes a real difference in our community
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {GIVING_TIERS.map((tier, index) => (
              <motion.div
                key={tier.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative bg-white rounded-2xl p-6 shadow-lg border-2 transition-all hover:scale-105 cursor-pointer ${
                  tier.popular ? 'border-red-400 ring-2 ring-red-100' : 'border-gold/20'
                }`}
                onClick={() => handleJoin(tier)}
                data-testid={`tier-${tier.id}`}
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-red-500 text-white text-xs font-bold rounded-full">
                    MOST POPULAR
                  </div>
                )}
                
                <div className="text-5xl text-center mb-4">{tier.icon}</div>
                
                <h3 className="font-serif text-xl text-center mb-2" style={{ color: '#1A1A1A' }}>
                  {tier.name}
                </h3>
                
                <div className="text-center mb-4">
                  <span className="text-3xl font-bold" style={{ color: tier.color }}>
                    ${tier.amount}
                  </span>
                  <span className="text-charcoal/60">/month</span>
                </div>
                
                <p className="text-sm text-center mb-6" style={{ color: '#1E3A5F' }}>
                  {tier.description}
                </p>
                
                <button
                  className="w-full py-3 rounded-full font-semibold text-white transition-colors"
                  style={{ backgroundColor: tier.color }}
                >
                  Join as {tier.name}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-6 bg-gradient-to-b from-cream to-green-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl p-8 md:p-12 shadow-xl"
          >
            <h2 className="font-serif text-2xl text-center mb-8" style={{ color: '#1A1A1A' }}>
              What You Get as a Fruit Bearer
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { icon: Calendar, text: 'Automatic monthly giving — set it and forget it' },
                { icon: Heart, text: 'Know your gift is working 24/7 for our community' },
                { icon: CheckCircle, text: 'Year-end giving statement for tax purposes' },
                { icon: Apple, text: 'Exclusive updates on ministry impact' }
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <item.icon size={20} className="text-green-600" />
                  </div>
                  <p className="text-charcoal/80 mt-2">{item.text}</p>
                </div>
              ))}
            </div>
            
            <p className="text-center mt-8 text-sm text-charcoal/60">
              Cancel anytime. Every dollar stays local.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6" style={{ backgroundColor: '#1A1A1A' }}>
        <div className="max-w-3xl mx-auto text-center text-white">
          <h2 className="font-serif text-3xl mb-4">Ready to Become a Fruit Bearer?</h2>
          <p className="text-white/80 mb-8">
            Choose your giving level above and join our family of faithful monthly givers today.
          </p>
          <button
            onClick={() => handleJoin(GIVING_TIERS[2])}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white font-semibold transition-transform hover:scale-105"
            style={{ color: '#1A1A1A' }}
            data-testid="join-fruit-bearers-cta"
          >
            <Apple size={20} />
            Join the Fruit Bearers Circle
          </button>
        </div>
      </section>

      {/* Modal */}
      {showModal && selectedTier && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="fruit-bearers-modal"
          >
            {/* Header */}
            <div className="p-6 text-center" style={{ backgroundColor: selectedTier.color }}>
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
              >
                <X size={20} className="text-white" />
              </button>
              <div className="text-5xl mb-2">{selectedTier.icon}</div>
              <h3 className="font-serif text-2xl text-white">{selectedTier.name}</h3>
              <p className="text-white/90 text-2xl font-bold">${selectedTier.amount}/month</p>
            </div>

            {/* Content */}
            <div className="p-6">
              {submitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="text-green-600" size={32} />
                  </div>
                  <h4 className="font-serif text-xl mb-2" style={{ color: '#1A1A1A' }}>
                    Thank You, Fruit Bearer!
                  </h4>
                  <p className="text-charcoal/70 mb-4">
                    Your ${selectedTier?.amount}/month contribution as a {selectedTier?.name} has been received.
                    You&apos;re now part of our faithful monthly giving family!
                  </p>
                  <p className="text-sm text-green-700 bg-green-50 p-3 rounded-xl mb-6">
                    A receipt has been sent to your email.<br />
                    <span className="text-xs">Living Fruit Ministry, Inc. | 501(c)(3) Pending</span>
                  </p>
                  <button
                    onClick={closeModal}
                    className="px-6 py-2 rounded-full text-white"
                    style={{ backgroundColor: selectedTier?.color || '#22c55e' }}
                  >
                    Close
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-4 text-center">
                    <p className="text-sm text-green-800">
                      <CreditCard size={14} className="inline mr-1" />
                      Secure payment via Stripe
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: '#1A1A1A' }}>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      maxLength={100}
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-green-400/50"
                      data-testid="fruit-bearer-name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: '#1A1A1A' }}>
                      Email *
                    </label>
                    <input
                      type="email"
                      required
                      maxLength={254}
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-green-400/50"
                      data-testid="fruit-bearer-email"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: '#1A1A1A' }}>
                      Phone (optional)
                    </label>
                    <input
                      type="tel"
                      maxLength={20}
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-green-400/50"
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3 rounded-full font-semibold text-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    style={{ backgroundColor: selectedTier.color }}
                    data-testid="fruit-bearer-submit"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Redirecting to payment...
                      </>
                    ) : (
                      <>
                        <CreditCard size={18} />
                        Pay ${selectedTier.amount}/month
                      </>
                    )}
                  </button>
                  
                  <p className="text-xs text-center text-charcoal/50">
                    You&apos;ll be redirected to Stripe for secure payment processing.
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
